import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SectionFrame from "../SectionFrame";
import { EditableButton, EditableText } from "../InlineEditable";
import EditableImage from "../EditableImage";

export default function ContactSection({ form, active, device, onSelect, onCommit, onUploadImage, onRemoveImage, disabled }) {
  const isMobile = device === "mobile";
  const sectionAppearance = form.appearance?.contact || {};
  const primary = sectionAppearance.accentColor || form.theme.primaryColor || "#F35A3A";
  const secondary = sectionAppearance.backgroundColor || form.theme.secondaryColor || "#19242F";
  const titleColor = sectionAppearance.titleColor || "#fff";
  const textColor = sectionAppearance.textColor || "rgba(255,255,255,.78)";
  const surfaceColor = sectionAppearance.surfaceColor || "rgba(255,255,255,.08)";

  return (
    <SectionFrame id="contact" label="Contacto" active={active} enabled={form.sections.showContact !== false} onSelect={onSelect} onToggle={(next) => onCommit("sections.showContact", next)}>
      <Box
        sx={{
          px: isMobile ? 2.5 : 5,
          pr: isMobile ? 2.5 : 15,
          py: isMobile ? "var(--wp-space-48)" : "var(--wp-space-64)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1.05fr) minmax(320px,.95fr)",
          gap: 4,
          bgcolor: secondary,
          color: "#fff",
        }}
      >
        <Box sx={{ alignSelf: "center" }}>
          <EditableText
            value={form.contact.title}
            fallback="Hablemos"
            disabled={disabled}
            onSave={(value) => onCommit("contact.title", value)}
            sx={{ color: titleColor, fontWeight: "var(--wp-segment-title-weight)", fontSize: isMobile ? "var(--wp-title-34)" : "var(--wp-title-46)", letterSpacing: "var(--wp-segment-letter-spacing)" }}
          />
          <Box sx={{ mt: 1.2, maxWidth: 620 }}>
            <EditableText
              value={form.contact.subtitle}
              fallback="Dejá una invitación clara para que tus clientes se comuniquen con el negocio."
              disabled={disabled}
              multiline
              minRows={3}
              onSave={(value) => onCommit("contact.subtitle", value)}
              sx={{ color: textColor, lineHeight: 1.7 }}
            />
          </Box>

          <Stack spacing={1.3} sx={{ mt: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnOutlinedIcon sx={{ color: primary }} />
              <Typography sx={{ color: "rgba(255,255,255,.88)", fontWeight: 750 }}>
                {String(form.business.address || "").trim() || "Dirección del negocio"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <ScheduleOutlinedIcon sx={{ color: primary }} />
              <Typography sx={{ color: "rgba(255,255,255,.88)", fontWeight: 750 }}>
                {String(form.business.schedule || "").trim() || "Horarios de atención"}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction={isMobile ? "column" : "row"} spacing={1.4} sx={{ mt: 3 }}>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <WhatsAppIcon />
              <EditableButton
                value={form.contact.whatsappCtaLabel}
                fallback="Escribir por WhatsApp"
                disabled={disabled}
                onSave={(value) => onCommit("contact.whatsappCtaLabel", value)}
                sx={{ bgcolor: primary, color: "#fff", borderRadius: 999, px: 2.5, py: 1.1, fontWeight: 950 }}
              />
            </Stack>
            <EditableButton
              value={form.contact.detailsCtaLabel}
              fallback="Ver contacto"
              disabled={disabled}
              variant="outlined"
              onSave={(value) => onCommit("contact.detailsCtaLabel", value)}
              sx={{ bgcolor: "transparent", color: "#fff", borderColor: "rgba(255,255,255,.62)", borderRadius: 999, px: 2.5, py: 1.05, fontWeight: 900 }}
            />
          </Stack>
        </Box>

        <EditableImage
          value={form.contact.backgroundImageUrl}
          label="Imagen o mapa de contacto"
          height={isMobile ? 250 : 340}
          disabled={disabled}
          onUpload={(file) => onUploadImage("contact.backgroundImageUrl", file, { sectionKey: "contact", assetKey: "background" })}
          onRemove={() => onRemoveImage("contact.backgroundImageUrl")}
          sx={{ bgcolor: surfaceColor, borderColor: "rgba(255,255,255,.34)", transform: "scale(var(--wp-segment-image-scale))", transformOrigin: "center" }}
        />
      </Box>
    </SectionFrame>
  );
}
